import { useState } from 'react'
import { SegmentedIndicator } from '@/components/SegmentedIndicator'
import { Button } from '@/components/ui/button'
import { categoryLabel, SITE_HEALTH_CATEGORIES } from '@/lib/siteHealth'
import type {
  SiteHealthCategory,
  SiteHealthFindingTypeReport,
} from '@/lib/siteHealth.types'
import FindingTypeDetails from './FindingTypeDetails'
import { CARD_CLASS, EYEBROW_CLASS } from './SiteHealthStyles'
import type {
  SiteHealthFindingTypeListProps,
  SiteHealthFindingTypeSelection,
} from './siteHealthComponents.types'

const DIMENSION_INDICATOR = 'site-health-dimension-indicator'

function selectionFor(
  findingTypes: SiteHealthFindingTypeReport[],
  category: SiteHealthCategory = findingTypes[0]?.category ?? 'access'
): SiteHealthFindingTypeSelection {
  return {
    activeCategory: category,
    expandedTypeKey:
      findingTypes.find((findingType) => findingType.category === category)
        ?.typeKey ?? null,
  }
}

export default function SiteHealthFindingTypeList({
  auditId,
  findingTypes,
}: SiteHealthFindingTypeListProps) {
  const [selection, setSelection] = useState(() => selectionFor(findingTypes))
  const activeReports = findingTypes.filter(
    (findingType) => findingType.category === selection.activeCategory
  )

  function selectCategory(category: SiteHealthCategory) {
    setSelection(selectionFor(findingTypes, category))
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
      <aside className={`${CARD_CLASS} h-fit p-3`}>
        <p className={`${EYEBROW_CLASS} px-3 py-2`}>Dimensions</p>
        <nav aria-label="Finding dimensions" className="space-y-1">
          {SITE_HEALTH_CATEGORIES.map((category) => {
            const count = findingTypes.filter(
              (findingType) => findingType.category === category
            ).length
            const active = selection.activeCategory === category

            return (
              <Button
                key={category}
                type="button"
                variant="ghost"
                aria-current={active ? 'page' : undefined}
                aria-label={`${categoryLabel(category)} ${count}`}
                onClick={() => selectCategory(category)}
                className={`relative h-auto w-full justify-between rounded-xl px-3 py-2.5 text-left hover:bg-transparent ${active ? 'font-medium text-primary' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {active && (
                  <SegmentedIndicator
                    layoutId={DIMENSION_INDICATOR}
                    className="absolute inset-0 rounded-xl bg-primary/10"
                  />
                )}
                <span className="relative">{categoryLabel(category)}</span>
                <span className="relative text-xs tabular-nums">{count}</span>
              </Button>
            )
          })}
        </nav>
      </aside>

      <div className="space-y-3">
        {activeReports.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
            No findings were returned for{' '}
            {categoryLabel(selection.activeCategory)}.
          </div>
        ) : (
          activeReports.map((report) => (
            <FindingTypeDetails
              key={report.typeKey}
              auditId={auditId}
              report={report}
              expanded={selection.expandedTypeKey === report.typeKey}
              onToggle={() =>
                setSelection((current) => ({
                  ...current,
                  expandedTypeKey:
                    current.expandedTypeKey === report.typeKey
                      ? null
                      : report.typeKey,
                }))
              }
            />
          ))
        )}
      </div>
    </div>
  )
}
